import { useCallback } from 'react';
import { fetchTopicData } from '../services/api';
import { saveHierarchyToFirestore, fetchHierarchyFromFirestore } from '../services/firestore';

const useEventHandlers = ({
  user,
  openSignInModal,
  setNodes,
  setEdges,
  setExpandedNodes,
  setExpandedContent,
  setIsLoading,
  setError,
  setIsSearchTriggered,
  setShowSearchSuggestions,
  setTopicData,
  setSessionId,
  nodes,
  sessionId,
  userId,
  createNodesFromData,
  createEdgesFromData,
  createHierarchicalLayout,
  setLoadingNodes,
  setNodesWithChildren,
  setChildrenExpanded,
}) => {
  // Handle the search button click
  const handleSearchClick = useCallback(
    async (topic) => {
      if (!topic.trim()) {
        setError('Topic cannot be empty');
        return;
      }

      setIsSearchTriggered(true);
      setIsLoading(true);
      setError(null);
      setShowSearchSuggestions(false);

      try {
        const data = await fetchTopicData(topic, sessionId);

        if (data?.result) {
          setTopicData(data.result);
          setSessionId(data.session_id || sessionId);

          const newNodes = createNodesFromData(data.result);
          const newEdges = createEdgesFromData(data.result);

          setNodes(newNodes);
          setEdges(newEdges);
          setExpandedNodes({});
          setExpandedContent({});

          if (user) {
            await saveHierarchyToFirestore(userId, data.session_id || sessionId, newNodes);
          }
        } else {
          setError(`No data found for the topic: "${topic}"`);
        }
      } catch (error) {
        setError(`Error during search: ${error.message}`);
        console.error('Search error:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      user,
      // openSignInModal,
      setNodes,
      setEdges,
      setExpandedNodes,
      setExpandedContent,
      setIsLoading,
      setError,
      setIsSearchTriggered,
      setShowSearchSuggestions,
      setTopicData,
      setSessionId,
      sessionId,
      userId,
      createNodesFromData,
      createEdgesFromData,
    ]
  );

  // Handle sidebar navigation clicks
  const handleNavbarClick = useCallback(
    async (topicContent) => {
      setIsLoading(true);
      setError(null);

      try {
        const hierarchyData = await fetchHierarchyFromFirestore(topicContent, user?.uid);

        if (!hierarchyData?.length) {
          setError(`No data found for the topic: "${topicContent}"`);
          return;
        }

        const newNodes = createNodesFromData(hierarchyData);
        const newEdges = createEdgesFromData(hierarchyData);

        const nodesWithChildrenSet = new Set(
          hierarchyData
            .filter((item) => hierarchyData.some((child) => child.parent_id === item.id))
            .map((item) => `${item.id}`)
        );

        setNodesWithChildren(nodesWithChildrenSet);
        setNodes(newNodes);
        setEdges(newEdges);
        setExpandedNodes({});
        setExpandedContent({});
      } catch (err) {
        console.error('Error fetching topic data:', err);
        setError('Failed to load the topic. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsLoading,
      setError,
      user,
      createNodesFromData,
      createEdgesFromData,
      setNodesWithChildren,
      setNodes,
      setEdges,
      setExpandedNodes,
      setExpandedContent,
    ]
  );

  // Handle the "Dive Deeper" functionality
  const handleDiveDeeper = useCallback(
    async (nodeId, header) => {
      if (!user) {
        openSignInModal();
        return;
      }

      const rootNode = nodes.find((node) => !node.parentNode);
      if (!rootNode) {
        console.error('Root node not found');
        return;
      }

      const mainTopic = rootNode.data.originalContent;
      const parentNode = nodes.find((node) => node.id === nodeId);
      const parent_id = parentNode?.id || 1;

      setLoadingNodes((prev) => new Set([...prev, nodeId]));

      try {
        const hierarchy = await fetchHierarchyFromFirestore(mainTopic, user?.uid);

        if (!hierarchy) {
          throw new Error('Failed to fetch the hierarchy');
        }

        const filteredHierarchyData = hierarchy.map(({ parent_id, id, header, content }) => ({ parent_id, id, header, content }))

        const data = await fetchTopicData(header, sessionId, parent_id, filteredHierarchyData);

        if (data?.result) {
          const { nodes: newNodes, edges: newEdges } = createHierarchicalLayout(data.result, parentNode);

          setNodes((prevNodes) => [...prevNodes, ...newNodes]);
          setEdges((prevEdges) => [...prevEdges, ...newEdges]);
          setNodesWithChildren((prev) => new Set([...prev, nodeId]));
          setChildrenExpanded((prev) => ({ ...prev, [nodeId]: true }));

          await saveHierarchyToFirestore(userId, data.session_id || sessionId, newNodes);
        }
      } catch (err) {
        console.error('Error fetching deeper data:', err);
      } finally {
        setLoadingNodes((prev) => {
          const next = new Set(prev);
          next.delete(nodeId);
          return next;
        });
      }
    },
    [
      user,
      openSignInModal,
      nodes,
      sessionId,
      setLoadingNodes,
      createHierarchicalLayout,
      setNodes,
      setEdges,
      setNodesWithChildren,
      setChildrenExpanded,
      userId,
    ]
  );

  return { handleSearchClick, handleNavbarClick, handleDiveDeeper };
};

export default useEventHandlers;
